import { useEffect, useState } from 'react';
import { Box, Button, Dialog, IconButton, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { eventUrl, type Opportunity } from '../../shared/opportunity';

/**
 * "Something wrong with this listing?"
 *
 * This is the only correction channel the pilot has. The dataset is a snapshot
 * taken on 2 September and events move, sell out and get canceled continuously
 * through the month, so reports are operationally important rather than a
 * nice-to-have — someone should be watching the queue during the week itself.
 *
 * The report is submitted here rather than handed off to a Google Form. A
 * hand-off was one more page, one more form, and a retyped event title between
 * noticing a problem and telling anyone about it, which is exactly the friction
 * that keeps corrections from arriving. `POST /reports` stores the report and
 * alerts the team; see `server/` for the endpoint behind it.
 */

const REPORTS_URL = import.meta.env.VITE_REPORTS_URL as string | undefined;

/** Where a report goes by hand when there is no endpoint, or it failed: the city file's `correctionsEmail`. */
const FALLBACK_EMAIL = 'hello@joincommonground.app';

const REASONS = [
  'Wrong date or time',
  'Wrong or missing location',
  'Event is canceled',
  'Link is broken',
  'Wrong topic or category',
  'Something else',
] as const;

/** Deliberately loose: the server is the authority, this only catches typos. */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Panel = styled('div')({
  position: 'relative',
  background: BRAND.mochaDeep,
  color: BRAND.parchment,
  padding: '28px 30px 26px',
});

const Choice = styled('button')<{ $on: boolean }>(({ $on }) => ({
  appearance: 'none',
  cursor: 'pointer',
  padding: '7px 13px',
  borderRadius: 999,
  fontFamily: FONTS.body,
  fontSize: 12.5,
  fontWeight: 600,
  background: $on ? BRAND.parchment : 'transparent',
  color: $on ? BRAND.mocha : BRAND.parchment,
  border: `1px solid ${$on ? BRAND.parchment : 'rgba(243,238,231,0.4)'}`,
  transition: 'background 120ms ease, color 120ms ease',
  '&:hover': { borderColor: BRAND.parchment },
}));

/** Shared by both text inputs, which differ only in their rows. */
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: BRAND.parchment,
    fontFamily: FONTS.body,
    fontSize: 14,
    '& fieldset': { borderColor: 'rgba(243,238,231,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(243,238,231,0.5)' },
    '&.Mui-focused fieldset': { borderColor: BRAND.parchment },
  },
  '& .MuiOutlinedInput-input::placeholder': { opacity: 0.55 },
} as const;

interface Props {
  event: Opportunity | null;
  onClose: () => void;
  /** The city file's `correctionsEmail`. */
  correctionsEmail?: string;
}

export default function ReportDialog({ event, onClose, correctionsEmail = FALLBACK_EMAIL }: Props) {
  const [reason, setReason] = useState<string | null>(null);
  const [detail, setDetail] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  useEffect(() => {
    if (event) {
      setReason(null);
      setDetail('');
      setEmail('');
      setState('idle');
    }
  }, [event]);

  const trimmedEmail = email.trim();
  const emailLooksWrong = trimmedEmail.length > 0 && !LOOKS_LIKE_EMAIL.test(trimmedEmail);

  const submit = async () => {
    if (!event || !reason || emailLooksWrong) return;

    setState('sending');
    try {
      // No endpoint configured is a deployment mistake, not a user error, but
      // it fails the same way from here: fall through to the error state, which
      // offers a route that does not depend on us having wired anything up.
      if (!REPORTS_URL) throw new Error('no endpoint configured');

      const res = await fetch(REPORTS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: event.id,
          event_url: eventUrl(event),
          event_title: event.name,
          reason,
          detail: detail.trim() || undefined,
          reporter_email: trimmedEmail || undefined,
          reported_at: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setState('sent');
      setTimeout(onClose, 1600);
    } catch {
      setState('error');
    }
  };

  /** Everything the user typed, so a failed send can still be sent by hand. */
  const mailtoFallback = event
    ? `mailto:${correctionsEmail}?subject=${encodeURIComponent(
        `Event report: ${event.name}`,
      )}&body=${encodeURIComponent(
        [
          `Event: ${event.name}`,
          `Id: ${event.id}`,
          `Source: ${event.sourceUrl}`,
          `Link: ${eventUrl(event)}`,
          `Problem: ${reason ?? '—'}`,
          detail.trim() ? `Details: ${detail.trim()}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
      )}`
    : `mailto:${correctionsEmail}`;

  return (
    <Dialog
      open={Boolean(event)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { background: BRAND.mochaDeep, backgroundImage: 'none', borderRadius: 3 } },
      }}
    >
      {event && (
        <Panel>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{ position: 'absolute', top: 12, right: 14, color: BRAND.parchment, zIndex: 1 }}
          >
            ✕
          </IconButton>

          <Typography
            sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 21, mb: 0.5, pr: 5 }}
          >
            Report a problem
          </Typography>
          <Typography sx={{ fontSize: 13, opacity: 0.7, mb: 2.5, pr: 5 }}>{event.name}</Typography>

          {state === 'sent' ? (
            <Typography sx={{ fontSize: 15, py: 3 }}>
              Thank you — that helps more than you'd think. We read every one.
              {trimmedEmail && ' We’ll be in touch if we need more detail.'}
            </Typography>
          ) : (
            <>
              <Typography
                sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.13em', opacity: 0.55, mb: 1.2 }}
              >
                WHAT'S WRONG?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.9, mb: 2.5 }}>
                {REASONS.map((r) => (
                  <Choice key={r} $on={reason === r} onClick={() => setReason(r)}>
                    {r}
                  </Choice>
                ))}
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={3}
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="Anything else we should know? (optional)"
                slotProps={{ htmlInput: { maxLength: 2000 } }}
                sx={{ mb: 1.6, ...fieldSx }}
              />

              {/* Optional on purpose. A correction is worth more than knowing
                  who sent it, so the address buys a reply and nothing else. */}
              <TextField
                fullWidth
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email if you'd like us to follow up about this report (optional)"
                error={emailLooksWrong}
                helperText={emailLooksWrong ? "That doesn't look like an email address." : undefined}
                slotProps={{ htmlInput: { maxLength: 254 } }}
                sx={{
                  mb: 2,
                  ...fieldSx,
                  '& .MuiFormHelperText-root': { fontFamily: FONTS.body, fontSize: 11.5 },
                }}
              />

              {state === 'error' && (
                <Typography sx={{ fontSize: 13, color: '#ffb9b9', mb: 1.5, lineHeight: 1.5 }}>
                  That didn't send. Check your connection and try again — or{' '}
                  <Box
                    component="a"
                    href={mailtoFallback}
                    sx={{ color: 'inherit', textUnderlineOffset: 3 }}
                  >
                    email it to us directly
                  </Box>
                  , with what you've written already filled in.
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1.2, justifyContent: 'flex-end' }}>
                <Button onClick={onClose} sx={{ color: 'rgba(243,238,231,0.7)', fontWeight: 600 }}>
                  Cancel
                </Button>
                <Button
                  // No endpoint in the static demo (contract §8): the report
                  // goes by email, prefilled, instead of being posted.
                  {...(REPORTS_URL ? { onClick: submit } : { href: mailtoFallback, onClick: () => setTimeout(onClose, 300) })}
                  disabled={!reason || emailLooksWrong || state === 'sending'}
                  sx={{
                    background: BRAND.parchment,
                    color: BRAND.mocha,
                    fontWeight: 700,
                    borderRadius: 999,
                    px: 3,
                    '&:hover': { background: '#fff' },
                    '&.Mui-disabled': { background: 'rgba(243,238,231,0.25)', color: 'rgba(54,47,42,0.5)' },
                  }}
                >
                  {state === 'sending' ? 'Sending…' : REPORTS_URL ? 'Submit report' : 'Email this report'}
                </Button>
              </Box>
            </>
          )}
        </Panel>
      )}
    </Dialog>
  );
}
